import React, { useState, useEffect } from 'react';
import { ChevronRight, Menu, X, ChevronDown } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Navbar = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrollY, setScrollY] = useState(0);
    const [businessDropdownOpen, setBusinessDropdownOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Function to handle smooth scrolling for homepage sections
    const scrollToSection = (sectionId) => {
        if (location.pathname !== '/') {
            navigate('/');
            // Add a small delay to allow navigation to complete before scrolling
            setTimeout(() => {
                const section = document.getElementById(sectionId);
                if (section) {
                    section.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
        } else {
            const section = document.getElementById(sectionId);
            if (section) {
                section.scrollIntoView({ behavior: 'smooth' });
            }
        }
        setIsMenuOpen(false);
    };

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.business-dropdown') && !event.target.closest('.business-trigger')) {
                setBusinessDropdownOpen(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // Handle scroll for navbar transparency
    useEffect(() => {
        const handleScroll = () => {
            setScrollY(window.scrollY);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <nav
            className={`fixed w-full z-50 transition-all duration-300 ${
                scrollY > 50
                    ? 'bg-zonex-white/95 backdrop-blur-md py-3 border-b border-zonex-gray-200 shadow-md'
                    : 'bg-transparent py-5'
            }`}
        >
            <div className="container mx-auto px-4 flex justify-between items-center">
                {/* Logo */}
                <Link to="/" className="flex items-center">
                    <img
                        src="/zonx.png"
                        alt="ZoneX"
                        className="h-8 w-auto object-contain"
                    />
                </Link>

                {/* Desktop Menu */}
                <div className="hidden md:flex items-center space-x-8">
                    {/* Main Navigation Links */}
                    <div className="flex space-x-6">
                        {location.pathname === '/' ? (
                            <>
                                <button
                                    onClick={() => scrollToSection('features')}
                                    className="text-zonex-gray-800 hover:text-zonex-red transition-colors"
                                >
                                    Features
                                </button>
                                <button
                                    onClick={() => scrollToSection('how-it-works')}
                                    className="text-zonex-gray-800 hover:text-zonex-red transition-colors"
                                >
                                    How It Works
                                </button>
                                 <button
                                    onClick={() => scrollToSection('spare-parts-section')}
                                    className="text-zonex-gray-800 hover:text-zonex-red transition-colors"
                                >
                                    Spares
                                </button>
                                <button
                                    onClick={() => scrollToSection('testimonials')}
                                    className="text-zonex-gray-800 hover:text-zonex-red transition-colors"
                                >
                                    Testimonials
                                </button>
                                
                            </>
                        ) : (
                            <Link
                                to="/"
                                className="text-zonex-gray-800 hover:text-zonex-red transition-colors"
                            >
                                Home
                            </Link>
                        )}

                        {/* Business Dropdown */}
                        <div className="relative business-trigger">
                            <button
                                onClick={() => setBusinessDropdownOpen(!businessDropdownOpen)}
                                className={`flex items-center transition-colors ${
                                    location.pathname.includes('/parking') || location.pathname.includes('/mechanics')
                                        ? 'text-zonex-red'
                                        : 'text-zonex-gray-800 hover:text-zonex-red'
                                }`}
                            >
                                Business <ChevronDown className="w-4 h-4 ml-1" />
                            </button>
                            
                            {/* Business Dropdown Menu */}
                            {businessDropdownOpen && (
                                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-2 z-50 business-dropdown">
                                    <Link
                                        to="/parking"
                                        className={`block px-4 py-2 text-sm hover:bg-zonex-gray-100 ${
                                            location.pathname.includes('/parking')
                                                ? 'text-zonex-red'
                                                : 'text-zonex-gray-800'
                                        }`}
                                        onClick={() => setBusinessDropdownOpen(false)}
                                    >
                                        Parking
                                    </Link>
                                    <Link
                                        to="/mechanics"
                                        className={`block px-4 py-2 text-sm hover:bg-zonex-gray-100 ${
                                            location.pathname.includes('/mechanics')
                                                ? 'text-zonex-red'
                                                : 'text-zonex-gray-800'
                                        }`}
                                        onClick={() => setBusinessDropdownOpen(false)}
                                    >
                                        Mechanics
                                    </Link>
                                </div>
                            )}
                        </div>

                        <Link
                            to="/about"
                            className={`transition-colors ${
                                location.pathname === '/about'
                                    ? 'text-zonex-red'
                                    : 'text-zonex-gray-800 hover:text-zonex-red'
                            }`}
                        >
                            About Us
                        </Link>

                        <Link
                            to="/contact"
                            className={`transition-colors ${
                                location.pathname === '/contact'
                                    ? 'text-zonex-red'
                                    : 'text-zonex-gray-800 hover:text-zonex-red'
                            }`}
                        >
                            Contact
                        </Link>
                    </div>

                    {/* Download Button */}
                    <Link
                        to="/contact"
                        className="bg-zonex-red text-white px-6 py-2 rounded-full hover:bg-zonex-darkRed transition-all duration-300 flex items-center shadow-red-md"
                    >
                        Download App <ChevronRight className="w-4 h-4 ml-1" />
                    </Link>
                </div>

                {/* Mobile Menu Button */}
                <button
                    className="md:hidden text-zonex-gray-800"
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    aria-label="Toggle menu"
                >
                    {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            {/* Mobile Menu */}
            <div
                className={`md:hidden bg-zonex-white absolute w-full transition-all duration-300 ease-in-out border-b border-zonex-gray-200 ${
                    isMenuOpen ? 'max-h-screen py-4 shadow-lg' : 'max-h-0 py-0 overflow-hidden'
                }`}
            >
                <div className="container mx-auto px-4 flex flex-col space-y-4">
                    {location.pathname === '/' ? (
                        <>
                            <button
                                onClick={() => scrollToSection('features')}
                                className="text-left text-zonex-gray-800 hover:text-zonex-red transition-colors py-2"
                            >
                                Features
                            </button>
                            <button
                                onClick={() => scrollToSection('how-it-works')}
                                className="text-left text-zonex-gray-800 hover:text-zonex-red transition-colors py-2"
                            >
                                How It Works
                            </button>
                            <button
                                    onClick={() => scrollToSection('spare-parts-section')}
                                    className="text-zonex-gray-800 hover:text-zonex-red transition-colors"
                                >
                                    Spares
                                </button>
                            <button
                                onClick={() => scrollToSection('testimonials')}
                                className="text-left text-zonex-gray-800 hover:text-zonex-red transition-colors py-2"
                            >
                                Testimonials
                            </button>
                           
                        </>
                    ) : (
                        <Link
                            to="/"
                            className="text-zonex-gray-800 hover:text-zonex-red transition-colors py-2"
                            onClick={() => setIsMenuOpen(false)}
                        >
                            Home
                        </Link>
                    )}

                    {/* Mobile Business Dropdown */}
                    <div>
                        <button
                            onClick={() => setBusinessDropdownOpen(!businessDropdownOpen)}
                            className="text-left w-full flex items-center justify-between text-zonex-gray-800 hover:text-zonex-red transition-colors py-2"
                        >
                            <span>Business</span>
                            <ChevronDown className={`w-4 h-4 transform transition-transform ${businessDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {businessDropdownOpen && (
                            <div className="pl-4 mt-1 space-y-2 border-l-2 border-zonex-gray-200">
                                <Link
                                    to="/parking"
                                    className={`block py-2 text-sm ${
                                        location.pathname.includes('/parking')
                                            ? 'text-zonex-red'
                                            : 'text-zonex-gray-800 hover:text-zonex-red'
                                    }`}
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    Parking
                                </Link>
                                <Link
                                    to="/mechanics"
                                    className={`block py-2 text-sm ${
                                        location.pathname.includes('/mechanics')
                                            ? 'text-zonex-red'
                                            : 'text-zonex-gray-800 hover:text-zonex-red'
                                    }`}
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    Mechanics
                                </Link>
                            </div>
                        )}
                    </div>

                    <Link
                        to="/about"
                        className={`py-2 transition-colors ${
                            location.pathname === '/about'
                                ? 'text-zonex-red'
                                : 'text-zonex-gray-800 hover:text-zonex-red'
                        }`}
                        onClick={() => setIsMenuOpen(false)}
                    >
                        About Us
                    </Link>

                    <Link
                        to="/contact"
                        className={`py-2 transition-colors ${
                            location.pathname === '/contact'
                                ? 'text-zonex-red'
                                : 'text-zonex-gray-800 hover:text-zonex-red'
                        }`}
                        onClick={() => setIsMenuOpen(false)}
                    >
                        Contact
                    </Link>

                    <div className="pt-2 border-t border-zonex-gray-200">
                        <Link
                            to="/contact"
                            className="bg-zonex-red text-white px-6 py-2 rounded-full hover:bg-zonex-darkRed transition-all duration-300 flex items-center shadow-red-md w-full justify-center"
                            onClick={() => setIsMenuOpen(false)}
                        >
                            Download App <ChevronRight className="w-4 h-4 ml-1" />
                        </Link>
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;